import React, { useState, useEffect, useRef } from 'react';
import { collection, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const DIGIT_H = 28;

/* ── Single rolling digit drum ── */
const DigitRoller = ({ digit }) => {
  const initialized = useRef(false);

  useEffect(() => {
    // After first render, enable transitions for all future updates
    initialized.current = true;
  }, []);

  if (digit === ',') {
    return (
      <span style={{
        display: 'inline-block',
        fontSize: '20px',
        fontWeight: '700',
        color: 'var(--text-color)',
        width: '7px',
        textAlign: 'center',
        lineHeight: `${DIGIT_H}px`,
        opacity: 0.35,
        userSelect: 'none',
      }}>
        ,
      </span>
    );
  }

  const d = parseInt(digit, 10);

  return (
    <div style={{
      display: 'inline-block',
      height: `${DIGIT_H}px`,
      width: '14px',
      overflow: 'hidden',
      position: 'relative',
      WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 28%, black 72%, transparent 100%)',
      maskImage:       'linear-gradient(to bottom, transparent 0%, black 28%, black 72%, transparent 100%)',
    }}>
      <div style={{
        transform: `translateY(${-d * DIGIT_H}px)`,
        transition: initialized.current
          ? 'transform 0.55s cubic-bezier(0.4, 0.0, 0.2, 1)'
          : 'none',
        willChange: 'transform',
      }}>
        {Array.from({ length: 10 }, (_, n) => (
          <div key={n} style={{
            height: `${DIGIT_H}px`,
            lineHeight: `${DIGIT_H}px`,
            textAlign: 'center',
            fontSize: '20px',
            fontWeight: '700',
            color: 'var(--text-color)',
            fontVariantNumeric: 'tabular-nums',
          }}>
            {n}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Renders the full number with stable per-position keys ── */
const OdometerDisplay = ({ value }) => {
  const formatted = value.toLocaleString();
  const chars = formatted.split('');

  // Key digits from the RIGHT so each column keeps its identity
  // when the number gains/loses a leading digit.
  let digitPos = 0;
  const keyed = [];
  for (let i = chars.length - 1; i >= 0; i--) {
    if (chars[i] === ',') {
      keyed.unshift({ ch: ',', key: `sep-${chars.length - i}` });
    } else {
      keyed.unshift({ ch: chars[i], key: `d${digitPos++}` });
    }
  }

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
      {keyed.map(({ ch, key }) => (
        <DigitRoller key={key} digit={ch} />
      ))}
    </div>
  );
};

/* ── Main counter ── */
const LiveCounter = () => {
  const [displayValue, setDisplayValue] = useState(null);
  const [pulse, setPulse] = useState(false);

  const triggerPulse = () => {
    setPulse(false);
    // Double rAF ensures the animation class is removed & re-added
    requestAnimationFrame(() => requestAnimationFrame(() => setPulse(true)));
    setTimeout(() => setPulse(false), 700);
  };

  useEffect(() => {
    const fetchInitialCount = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'grammarFixes'));
        setDisplayValue(134 + snapshot.size);
      } catch {
        setDisplayValue(134);
      }
    };
    fetchInitialCount();

    const unsubscribe = onSnapshot(
      collection(db, 'grammarFixes'),
      (snapshot) => {
        setDisplayValue(134 + snapshot.size);
        triggerPulse();
      },
      (err) => console.log('Counter listener error:', err)
    );
    return () => unsubscribe();
  }, []);

  if (displayValue === null) return null;

  return (
    <>
      <style>{`
        @keyframes odoGlow {
          0%   { filter: brightness(1)   drop-shadow(0 0  0px transparent); }
          35%  { filter: brightness(1.3) drop-shadow(0 0 10px var(--accent-color, #4f9eff)); }
          100% { filter: brightness(1)   drop-shadow(0 0  0px transparent); }
        }
        .odo-pulse { animation: odoGlow 0.65s ease-out forwards; }
      `}</style>

      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 999,
        textAlign: 'right',
        fontFamily: 'inherit',
      }}>
        <div style={{
          fontSize: '10px',
          color: 'var(--text-secondary)',
          marginBottom: '4px',
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}>
          Total Fixes
        </div>

        <div className={pulse ? 'odo-pulse' : ''}>
          <OdometerDisplay value={displayValue} />
        </div>
      </div>
    </>
  );
};

export default LiveCounter;